/**
 * Per-state SEO data for /stop-foreclosure-in/:state pages.
 * Each entry ranks the state's foreclosure specifics — judicial vs non-judicial,
 * typical timelines, sheriff-sale vs trustee-sale terminology — to earn organic
 * traffic on state-specific "stop foreclosure in {state}" queries.
 */
export const STATES = {
  "texas": {
    name: "Texas",
    abbrev: "TX",
    procedure: "Non-judicial (deed of trust)",
    saleName: "Substitute trustee sale",
    saleDay: "First Tuesday of the month",
    noticeDays: 21,
    timeToSale: "As short as 41 days from Notice of Default",
    counties: ["Harris", "Dallas", "Tarrant", "Bexar", "Travis", "Collin"],
    quickFact: "Texas allows the fastest non-judicial foreclosure in the U.S. — as little as 41 days.",
    localHook: "Substitute trustee sales are held at the county courthouse steps on the first Tuesday of every month. Timing is everything.",
  },
  "florida": {
    name: "Florida",
    abbrev: "FL",
    procedure: "Judicial (court-supervised)",
    saleName: "Clerk of Court foreclosure sale",
    saleDay: "Scheduled by county Clerk of Court",
    noticeDays: 20,
    timeToSale: "6 to 18 months typically",
    counties: ["Miami-Dade", "Broward", "Orange", "Palm Beach", "Hillsborough", "Duval"],
    quickFact: "Florida is judicial — a counterclaim can be filed directly in the same court hearing the foreclosure.",
    localHook: "Because Florida requires the bank to sue you, we can counter-sue inside the same case. That is the fastest structural advantage in the country.",
  },
  "california": {
    name: "California",
    abbrev: "CA",
    procedure: "Non-judicial (deed of trust)",
    saleName: "Trustee sale",
    saleDay: "Posted 20 days before at the property",
    noticeDays: 90,
    timeToSale: "About 200 days from Notice of Default",
    counties: ["Los Angeles", "San Diego", "Orange", "Riverside", "San Bernardino", "Alameda"],
    quickFact: "California requires a 90-day Notice of Default before any trustee sale can be recorded — that window is our filing window.",
    localHook: "The 90-day NoD period in California gives the Guild room to file a counterclaim and pledge trustee funds before a Notice of Sale is even recorded.",
  },
  "ohio": {
    name: "Ohio",
    abbrev: "OH",
    procedure: "Judicial (court-supervised)",
    saleName: "Sheriff sale",
    saleDay: "Set by county sheriff, usually Fridays",
    noticeDays: 30,
    timeToSale: "6 to 12 months typically",
    counties: ["Cuyahoga", "Franklin", "Hamilton", "Summit", "Lucas", "Montgomery"],
    quickFact: "Ohio ranks in the top three states for per-capita foreclosure filings — and it is judicial, which means the courts hear our counterclaim first.",
    localHook: "Cuyahoga and Franklin county sheriff sales are our most common intake. The counterclaim files in the same case and the sheriff pauses pending its hearing.",
  },
  "illinois": {
    name: "Illinois",
    abbrev: "IL",
    procedure: "Judicial (court-supervised)",
    saleName: "Judicial sale",
    saleDay: "Set by court, usually held at courthouse",
    noticeDays: 30,
    timeToSale: "9 to 15 months typically",
    counties: ["Cook", "DuPage", "Lake", "Will", "Kane", "McHenry"],
    quickFact: "Illinois requires a full judicial process — a counterclaim in that case is the natural, court-recognized answer.",
    localHook: "Cook County sees the highest foreclosure filing volume in the state. Every one of those cases can hold a counterclaim.",
  },
  "new-york": {
    name: "New York",
    abbrev: "NY",
    procedure: "Judicial (court-supervised)",
    saleName: "Judicial sale by referee",
    saleDay: "Set by court after judgment",
    noticeDays: 90,
    timeToSale: "18 to 36 months typically",
    counties: ["Kings", "Queens", "Suffolk", "Nassau", "Bronx", "Westchester"],
    quickFact: "New York has one of the longest foreclosure timelines in the country — giving the Guild extensive runway to file, mediate, and settle.",
    localHook: "The mandatory 90-day pre-foreclosure notice under NY RPAPL §1304 is the perfect intake window for a Guild ledger audit.",
  },
  "pennsylvania": {
    name: "Pennsylvania",
    abbrev: "PA",
    procedure: "Judicial (court-supervised)",
    saleName: "Sheriff sale",
    saleDay: "Held monthly at county courthouse",
    noticeDays: 30,
    timeToSale: "9 to 14 months typically",
    counties: ["Philadelphia", "Allegheny", "Montgomery", "Bucks", "Delaware", "Chester"],
    quickFact: "Pennsylvania sheriff sales are held in every county monthly — and every sale is subject to court-heard counterclaims.",
    localHook: "Philadelphia and Allegheny sheriff sales fill our emergency intake queue. Cases filed 10+ days before sale date have the highest halt rate.",
  },
  "georgia": {
    name: "Georgia",
    abbrev: "GA",
    procedure: "Non-judicial (security deed)",
    saleName: "Trustee sale on courthouse steps",
    saleDay: "First Tuesday of the month",
    noticeDays: 30,
    timeToSale: "As short as 32 days from Notice of Default",
    counties: ["Fulton", "Gwinnett", "DeKalb", "Cobb", "Clayton", "Cherokee"],
    quickFact: "Georgia is one of the fastest non-judicial states — 32 days is possible. The Guild&apos;s emergency triage is designed for exactly this.",
    localHook: "Fulton, Gwinnett and DeKalb sales all happen on the same Tuesday morning. If your notice is dated, the clock is already running.",
  },
  "north-carolina": {
    name: "North Carolina",
    abbrev: "NC",
    procedure: "Power-of-sale (deed of trust)",
    saleName: "Trustee sale",
    saleDay: "Scheduled after clerk hearing",
    noticeDays: 20,
    timeToSale: "3 to 5 months typically",
    counties: ["Mecklenburg", "Wake", "Guilford", "Forsyth", "Cumberland", "Durham"],
    quickFact: "North Carolina requires a clerk of court hearing before any sale — that hearing is the counterclaim&apos;s ideal filing point.",
    localHook: "Mecklenburg and Wake are our most common NC intakes. The clerk hearing is public record — and a filed counterclaim delays it.",
  },
  "arizona": {
    name: "Arizona",
    abbrev: "AZ",
    procedure: "Non-judicial (deed of trust)",
    saleName: "Trustee sale",
    saleDay: "Held 90+ days after Notice of Sale",
    noticeDays: 90,
    timeToSale: "About 120 days from Notice of Sale",
    counties: ["Maricopa", "Pima", "Pinal", "Yavapai", "Mohave", "Yuma"],
    quickFact: "Arizona's 90-day Notice of Sale window is a Guild sweet spot — long enough to file, short enough to force action.",
    localHook: "Maricopa County sees the highest foreclosure activity in the state. Every trustee sale there is announceable and countervailable.",
  },
};

export const STATE_SLUGS = Object.keys(STATES);
